import { useSelector } from "react-redux";
import moment from "moment";
import {
  assetSelector,
  applicationSelector,
  loanDetailsSelector,
  // @ts-expect-error: ignoring as it is existing js file
} from "@/store/slices/applicationFormSlice";
import {
  type ApplicationFormSlice,
  type ApplicationState,
  type LoanDetails,
  type Asset,
  ApplicationType,
} from "@/store/slices/types/applicationFormSlice";
import { getIsAssetLoan, getIsCommercialOrBusinessApplication } from "@/constants";
// @ts-expect-error: ignoring as it is existing js file
import calculateEffectiveRate from "@/utils/calculateEffectiveRate";
// @ts-expect-error existing utils
import calculateRepayments from "@/utils/calculateRepayments";
// @ts-expect-error existing utils
import getMonthlyRate from "@/utils/getMonthlyRate";

const useCalculateApplication = () => {
  const { assetValue } = useSelector<ApplicationFormSlice, Asset>(assetSelector);
  const { applicationType, asset, loanDetails, entities, customers } = useSelector<
    ApplicationFormSlice,
    ApplicationState
  >(applicationSelector);
  const {
    deposit,
    tradeIn,
    payout,
    requestedLoanAmount,
    term = 0,
  } = useSelector<ApplicationFormSlice, LoanDetails>(loanDetailsSelector);
  const isAssetLoan = getIsAssetLoan(applicationType);
  const isCommercialOrBusinessApplication = getIsCommercialOrBusinessApplication(applicationType);
  const netAssetValue = assetValue - deposit - tradeIn + payout;
  const principalAmount = isAssetLoan ? netAssetValue : requestedLoanAmount;

  const getBrokeragePercentage = ({ brokerageAmount = 0 }: { brokerageAmount: number | undefined }) => {
    const brokeragePercentage = (brokerageAmount / principalAmount) * 100;
    const roundedBrokeragePercentage = Number(brokeragePercentage.toFixed(2));
    return isFinite(roundedBrokeragePercentage) ? roundedBrokeragePercentage : 0;
  };

  const getBrokerageAmount = ({ brokeragePercentage = 0 }: { brokeragePercentage: number | undefined }) => {
    const brokerageAmount = (brokeragePercentage / 100) * principalAmount;
    const roundedBrokerageAmount = Number(brokerageAmount.toFixed(2));
    return roundedBrokerageAmount;
  };

  const getFeeAmount = ({ feePercentage = 0 }: { feePercentage: number | undefined }) => {
    return (feePercentage / 100) * principalAmount;
  };

  const getBalloonPercentage = ({ balloonAmount = 0 }: { balloonAmount: number | undefined }) => {
    const balloonPercentage = (balloonAmount / netAssetValue) * 100;
    return isFinite(balloonPercentage) ? balloonPercentage : 0;
  };

  const getBalloonAmount = ({ balloonPercentage = 0 }: { balloonPercentage: number | undefined }) => {
    return (balloonPercentage / 100) * netAssetValue;
  };

  const calculateQuoteRepayments = ({
    financeAmount,
    rate,
    balloon,
    isInArrears,
  }: {
    financeAmount: number;
    rate: number;
    balloon: number;
    isInArrears: boolean;
  }) => {
    const monthlyRate = getMonthlyRate(rate);
    // Future value of a non asset loan is 0.
    const futureValue = isAssetLoan ? (balloon / 100) * netAssetValue : 0;
    const repayments = calculateRepayments(monthlyRate, term, -financeAmount, futureValue, isInArrears ? 0 : 1);
    return Number(repayments.toFixed(2));
  };

  const getLoanAmount = ({ totalFinancedFees }: { totalFinancedFees: number }) => {
    return principalAmount + totalFinancedFees;
  };

  const getFinanceAmount = ({
    totalFinancedFees,
    brokerageAmount,
  }: {
    totalFinancedFees: number;
    brokerageAmount: number;
  }) => {
    switch (applicationType) {
      case ApplicationType.Commercial:
        return netAssetValue + totalFinancedFees + brokerageAmount;
      case ApplicationType.Consumer:
        return netAssetValue + totalFinancedFees;
      case ApplicationType.Personal:
        return requestedLoanAmount + totalFinancedFees;
      case ApplicationType.Business:
        return requestedLoanAmount + totalFinancedFees + brokerageAmount;
      default:
        return 0;
    }
  };

  const getEffectiveRate = ({
    repayments,
    totalFinancedFees,
    balloonAmount,
    rate,
    isInArrears,
  }: {
    repayments: number;
    totalFinancedFees: number;
    balloonAmount: number;
    rate: number;
    isInArrears: boolean;
  }) => {
    const effectiveRate = calculateEffectiveRate(
      term,
      repayments,
      -(principalAmount + totalFinancedFees),
      balloonAmount,
      isInArrears ? 0 : 1,
      Number(rate) / 100,
    );
    if (effectiveRate < rate) {
      return rate;
    }
    return effectiveRate;
  };

  const getComparisonRate = ({
    repayments,
    financeAmount,
    totalUpfrontFees,
    totalFinancedFees,
    brokerageAmount,
    balloonAmount,
    rate,
    isInArrears,
  }: {
    repayments: number;
    financeAmount: number;
    totalUpfrontFees: number;
    totalFinancedFees: number;
    brokerageAmount: number;
    balloonAmount: number;
    rate: number;
    isInArrears: boolean;
  }) => {
    // Present value.
    const presentValue = balloonAmount / Math.pow(1 + Number(rate), term);
    // True loan amount.
    const trueLoanAmount = financeAmount - presentValue;
    const comparisonRate = calculateEffectiveRate(
      term,
      repayments,
      -(
        trueLoanAmount -
        totalUpfrontFees -
        totalFinancedFees -
        (isCommercialOrBusinessApplication ? brokerageAmount : 0)
      ),
      balloonAmount,
      isInArrears ? 0 : 1,
      Number(rate) / 100,
    );
    if (comparisonRate < rate) {
      return rate;
    }
    return comparisonRate;
  };

  const getValuesToEvaluateCriteriaAgainst = () => {
    // These are values that are set in the application.
    // We transform and calculate some values, which then can be passed in to match criteria (in fees, etc).
    const values = {
      asset: {
        ...asset,
        ageOfAsset: asset?.ageOfAsset ? moment().diff(asset.ageOfAsset, "months") : undefined,
      },
      loanDetails: {
        ...loanDetails,
        loanAmount: principalAmount,
      },
      entity: {
        ...entities?.[0],
        gst: entities?.[0]?.gst ? moment().diff(entities?.[0]?.gst, "months") : undefined,
        timeInBusiness: entities?.[0]?.timeInBusiness
          ? moment().diff(entities?.[0]?.timeInBusiness, "months")
          : undefined,
      },
      customer: customers?.[0],
      customerProfile: customers?.[0]?.customerProfile,
      creditProfile: customers?.[0]?.creditProfile,
      employer: customers?.[0]?.employers?.[0],
    };
    return values;
  };

  const getNetAssetValueProvidedSingleField = ({
    fieldName,
    fieldValue,
  }: {
    fieldName: "assetValue" | "deposit" | "tradeIn" | "payout";
    fieldValue: number | string;
  }) => {
    const fieldValueNumber = Number(fieldValue);
    if (fieldName === "assetValue") {
      return fieldValueNumber - deposit - tradeIn + payout;
    }
    if (fieldName === "deposit") {
      return assetValue - fieldValueNumber - tradeIn + payout;
    }
    if (fieldName === "tradeIn") {
      return assetValue - deposit - fieldValueNumber + payout;
    }
    if (fieldName === "payout") {
      return assetValue - deposit - tradeIn + fieldValueNumber;
    }
  };

  return {
    getBrokeragePercentage,
    getBrokerageAmount,
    getFeeAmount,
    getBalloonPercentage,
    getBalloonAmount,
    calculateQuoteRepayments,
    getLoanAmount,
    getFinanceAmount,
    getEffectiveRate,
    getComparisonRate,
    getValuesToEvaluateCriteriaAgainst,
    getNetAssetValueProvidedSingleField,
  };
};

export { useCalculateApplication };
